import React, { useState, useEffect, useMemo } from "react";
import { Award, BarChart3, Plus, Pencil, Trash2, X } from "lucide-react";
import { C } from "../igualdad-data.js";
import { Status, SectionHead } from "../igualdad-components.jsx";
import { IgualdadChart } from "../igualdad-charts.jsx";
import { apiGetAll, apiGet, apiPost, apiPut, apiDelete } from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Evaluaciones
   ============================================================ */

const RESULT_BUCKETS = ["A", "B", "C", "D"];

function IgualdadEvaluaciones() {
  const { session, fireToast } = useIgualdad();
  const politicaId = session.politicaId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [evals, setEvals] = useState([]);
  const [sel, setSel] = useState(null);
  const [cat, setCat] = useState([]); // catálogo de competencias (deben ser 6)
  const [personas, setPersonas] = useState([]);

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState(null);
  const [form, setForm] = useState({ valores: {} });
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    if (!politicaId) { setLoading(false); setEvals([]); return; }
    setLoading(true); setError(null);
    try {
      const [list, comps, pers] = await Promise.all([
        apiGetAll("/evaluaciones", { politica_id: politicaId }),
        apiGetAll("/competencias", { politica_id: politicaId }),
        apiGetAll("/personas", { politica_id: politicaId }),
      ]);
      const filas = list.map((ev) => ({
        id: ev.id,
        nombre: ev.persona?.nombre || "—",
        puesto: ev.persona?.puesto?.nombre || "—",
        grupo: ev.persona?.grupo || "—",
        nota: Number(ev.nota) || 0,
        bucket: ev.bucket,
        label: ev.bucket_label || ev.bucket,
        comps: (ev.competencias || []).map((c) => ({ k: c.competencia?.nombre || "—", v: c.valor })),
      }));
      setEvals(filas);
      setCat(comps.filter((c) => c.activa));
      setPersonas(pers);
      setSel(filas[0]?.id ?? null);
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [politicaId]);

  const seisComp = cat.length === 6;

  function openNew() {
    setEditing(null);
    const ejercicio = session.politica?.ejercicio || "";
    const valores = {};
    cat.forEach((c) => { valores[c.id] = 3; });
    setForm({ persona_id: "", ciclo: `Desempeño ${ejercicio}`, comentario: "", fecha: "", valores });
    setFormErr(null);
    setModal(true);
  }

  async function openEdit(x) {
    setFormErr(null);
    try {
      const res = await apiGet(`/evaluaciones/${x.id}`);
      const r = res.data || res;
      const valores = {};
      cat.forEach((c) => { valores[c.id] = 3; });
      (r.competencias || []).forEach((cc) => { valores[cc.competencia_id] = cc.valor; });
      setEditing(r);
      setForm({ persona_id: r.persona_id, ciclo: r.ciclo, comentario: r.comentario ?? "", fecha: r.fecha ?? "", valores });
      setModal(true);
    } catch (e) { fireToast(e.message || "No se pudo cargar la evaluación."); }
  }

  function payload() {
    return {
      persona_id: Number(form.persona_id),
      politica_id: politicaId,
      ciclo: form.ciclo,
      comentario: form.comentario === "" ? null : form.comentario,
      fecha: form.fecha === "" ? null : form.fecha,
      competencias: cat.map((c) => ({ competencia_id: c.id, valor: Number(form.valores[c.id]) })),
    };
  }

  async function save() {
    if (saving) return;
    setSaving(true); setFormErr(null);
    try {
      if (editing) await apiPut(`/evaluaciones/${editing.id}`, payload());
      else await apiPost("/evaluaciones", payload());
      setModal(false);
      fireToast(editing ? "Evaluación actualizada." : "Evaluación creada.");
      await load();
    } catch (e) {
      setFormErr(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo guardar."));
    } finally { setSaving(false); }
  }

  async function remove() {
    if (deleting || !confirmDel) return;
    setDeleting(true);
    try {
      await apiDelete(`/evaluaciones/${confirmDel.id}`);
      fireToast("Evaluación eliminada.");
      setConfirmDel(null);
      await load();
    } catch (e) { fireToast(e.message || "No se pudo eliminar."); }
    finally { setDeleting(false); }
  }

  const cur = useMemo(() => evals.find((x) => x.id === sel) || evals[0] || null, [evals, sel]);
  const dist = useMemo(() => {
    const m = { A: 0, B: 0, C: 0, D: 0 };
    evals.forEach((x) => { if (m[x.bucket] !== undefined) m[x.bucket]++; });
    return RESULT_BUCKETS.map((b) => ({ name: b, value: m[b] }));
  }, [evals]);

  const radarOpt = useMemo(() => ({
    radar: {
      indicator: (cur?.comps || []).map((c) => ({ name: c.k, max: 5 })),
      radius: "68%",
      axisName: { fontSize: 12, color: C.textMuted },
      splitLine: { lineStyle: { color: C.border } },
      splitArea: { show: false },
      axisLine: { lineStyle: { color: C.border } },
    },
    series: [{
      type: "radar",
      symbolSize: 4,
      data: [{ value: (cur?.comps || []).map((c) => c.v), areaStyle: { color: "rgba(255, 137, 1, 0.28)" }, lineStyle: { color: C.brand }, itemStyle: { color: C.brand } }],
    }],
  }), [cur]);

  const distBar = useMemo(() => {
    const colors = [C.brandHover, C.brand, C.textMuted, C.danger];
    return {
      grid: { left: 30, right: 8, top: 16, bottom: 24 },
      tooltip: { trigger: "axis", backgroundColor: C.surface, borderColor: C.border, borderWidth: 1, extraCssText: "border-radius:8px;" },
      xAxis: { type: "category", data: dist.map((d) => d.name), axisLabel: { fontSize: 12, color: C.textMuted }, axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false } },
      yAxis: { type: "value", axisLine: { show: false }, axisTick: { show: false }, axisLabel: { fontSize: 12, color: C.textMuted }, splitLine: { lineStyle: { color: C.border } } },
      series: [{ type: "bar", data: dist.map((d, i) => ({ value: d.value, itemStyle: { color: colors[i], borderRadius: [4, 4, 0, 0] } })) }],
    };
  }, [dist]);

  return (
    <div className="rb-page rb-evals">
      <div className="rb-pagehead">
        <h1 className="rb-h1">Evaluaciones · Modelo {session.politica?.ejercicio}: Desempeño</h1>
        <button className="rb-btn-soft" onClick={openNew} disabled={!politicaId}><Plus size={14} /> Nueva evaluación</button>
      </div>
      {(loading || error)
        ? <Status loading={loading} error={error} />
        : !evals.length
          ? <Status empty={true} emptyText="No hay evaluaciones para esta política." />
          : (
            <>
              <div className="rb-grid2-2">
                <div className="rb-card">
                  <div className="rb-eval-h">
                    <SectionHead icon={Award} title="Evaluación por competencias" />
                    <select className="rb-sel-inline" value={sel ?? ""} onChange={(ev) => setSel(Number(ev.target.value))}>
                      {evals.map((x) => <option key={x.id} value={x.id}>{x.nombre} — {x.puesto}</option>)}
                    </select>
                  </div>
                  <IgualdadChart option={radarOpt} style={{ height: 300 }} />
                  {cur ? (
                    <div className="rb-eval-foot">
                      <span>Nota media <b>{cur.nota.toFixed(2).replace(".", ",")}</b> / 5</span>
                      <span className="rb-evchip" data-b={cur.bucket} style={{ fontSize: "var(--text-xs)" }}>{cur.label}</span>
                    </div>
                  ) : null}
                </div>

                <div className="rb-card">
                  <SectionHead icon={BarChart3} title="Distribución de resultados" />
                  <IgualdadChart option={distBar} style={{ height: 260 }} />
                  <div className="rb-note">Resultado A excelente · B cumple alto · C cumple · D mejorable. Alimenta la matriz de incrementos.</div>
                </div>
              </div>

              <div className="rb-card rb-card-flat">
                <table className="rb-table">
                  <thead><tr><th className="rb-l">Persona</th><th className="rb-l">Puesto</th><th className="rb-l">Grupo</th><th>Nota media</th><th>Resultado</th><th className="rb-actions-h"></th></tr></thead>
                  <tbody>
                    {evals.slice(0, 30).map((x) => (
                      <tr key={x.id} className={x.id === sel ? "rb-rowsel" : ""} onClick={() => setSel(x.id)} style={{ cursor: "pointer" }}>
                        <td className="rb-l rb-strong">{x.nombre}</td>
                        <td className="rb-l rb-soft">{x.puesto}</td>
                        <td className="rb-l rb-soft">{x.grupo}</td>
                        <td className="rb-mono">{x.nota.toFixed(2).replace(".", ",")}</td>
                        <td><span className="rb-evchip" data-b={x.bucket}>{x.label}</span></td>
                        <td className="rb-actions" onClick={(ev) => ev.stopPropagation()}>
                          <button className="rb-ico-btn" title="Editar" onClick={() => openEdit(x)}><Pencil size={15} /></button>
                          <button className="rb-ico-btn rb-danger" title="Eliminar" onClick={() => setConfirmDel(x)}><Trash2 size={15} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="rb-tablefoot">Mostrando {Math.min(30, evals.length)} de {evals.length} · pulsa una fila para ver su radar de competencias</div>
              </div>
            </>
          )}

      {modal ? (
        <div className="rb-modal-bg" onClick={() => setModal(false)}>
          <div className="rb-modal rb-modal-wide-evaluaciones" onClick={(ev) => ev.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">{editing ? "Editar evaluación" : "Nueva evaluación"}</div><div className="rb-modal-s">Modelo {session.politica?.ejercicio}: Desempeño</div></div>
              <button className="rb-ico" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b">
              {!seisComp
                ? <div className="rb-warn">Se requieren exactamente 6 competencias activas en el catálogo para registrar una evaluación (hay {cat.length}). Ajústalo en Catálogos → Competencias.</div>
                : (
                  <>
                    <div className="rb-grid2-form">
                      <div>
                        <label>Persona</label>
                        {!editing
                          ? <select value={form.persona_id} onChange={(ev) => setForm({ ...form, persona_id: ev.target.value })}><option value="">— Selecciona —</option>{personas.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}</select>
                          : <input className="rb-inp rb-inp-font" value={editing.persona?.nombre || ("#" + editing.persona_id)} disabled />}
                      </div>
                      <div><label>Ciclo</label><input className="rb-inp rb-inp-font" value={form.ciclo} onChange={(ev) => setForm({ ...form, ciclo: ev.target.value })} /></div>
                      <div><label>Fecha</label><input className="rb-inp rb-inp-font" type="date" value={form.fecha} onChange={(ev) => setForm({ ...form, fecha: ev.target.value })} /></div>
                    </div>
                    <label style={{ marginTop: 12, display: "block" }}>Valoración de competencias (1–5)</label>
                    {cat.map((c) => (
                      <div className="rb-comprow" key={c.id}>
                        <span>{c.nombre}</span>
                        <select value={form.valores[c.id]} onChange={(ev) => setForm({ ...form, valores: { ...form.valores, [c.id]: ev.target.value } })}>{[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}</select>
                      </div>
                    ))}
                    <label style={{ marginTop: 12, display: "block" }}>Comentario</label>
                    <textarea className="rb-inp rb-inp-font" rows={3} value={form.comentario} onChange={(ev) => setForm({ ...form, comentario: ev.target.value })} />
                  </>
                )}
              {formErr ? <div className="rb-formerr">{formErr}</div> : null}
            </div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
              <button className="rb-btn" disabled={saving || !seisComp} onClick={save}>{saving ? "Guardando…" : (editing ? "Guardar cambios" : "Crear evaluación")}</button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmDel ? (
        <div className="rb-modal-bg" onClick={() => setConfirmDel(null)}>
          <div className="rb-modal rb-modal-sm" onClick={(ev) => ev.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">Eliminar evaluación</div><div className="rb-modal-s">Esta acción no se puede deshacer.</div></div>
              <button className="rb-ico" onClick={() => setConfirmDel(null)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b"><p className="rb-soft">¿Eliminar la evaluación de <b>{confirmDel.nombre}</b>?</p></div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setConfirmDel(null)}>Cancelar</button>
              <button className="rb-btn rb-btn-danger" disabled={deleting} onClick={remove}>{deleting ? "Eliminando…" : "Eliminar"}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { IgualdadEvaluaciones };
